import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { AlertCircle } from "lucide-react";
import AuthPageShell from "../components/auth/AuthPageShell";
import {
  AuthTitle,
  AuthSub,
  Divider,
  Label,
  Input,
  Field,
  Submit,
  Err,
  Footer,
} from "../components/auth/authFormStyles";
import { cPath } from "../constants";
import { completePasswordReset } from "../store/actions/authActions";
import useSEO from "../hooks/useSEO";

const Back = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
  &:hover {
    text-decoration: underline;
  }
`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authError = useSelector((s) => s.auth.error);
  const loading = useSelector((s) => s.auth.loading);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [localErr, setLocalErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLocalErr(null);
    if (password.length < 8) {
      setLocalErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setLocalErr("Passwords do not match.");
      return;
    }
    if (!token) {
      setLocalErr("Missing token in URL. Open the link from your reset email.");
      return;
    }
    const ok = await dispatch(completePasswordReset(token, password));
    if (ok) {
      navigate(cPath("dashboard"), { replace: true });
    }
  };

  const errMsg = localErr || authError;
  const seo = useSEO({ title: "Reset Password", noindex: true, nofollow: true });

  return (
    <>
      {seo}
      <AuthPageShell heroHint="Choose a new password to secure your account.">
      <Divider />
      <AuthTitle>Set new password</AuthTitle>
      <AuthSub>Choose a strong password for your DealBeez account.</AuthSub>
      {errMsg ? (
        <Err role="alert">
          <AlertCircle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{errMsg}</span>
        </Err>
      ) : null}
      <form onSubmit={submit}>
        <Field>
          <Label htmlFor="np1">New password</Label>
          <Input
            id="np1"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            placeholder="At least 8 characters"
          />
        </Field>
        <Field>
          <Label htmlFor="np2">Confirm password</Label>
          <Input
            id="np2"
            type="password"
            autoComplete="new-password"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            required
            minLength={8}
            placeholder="Repeat password"
          />
        </Field>
        <Submit type="submit" disabled={loading}>
          {loading ? "Saving…" : "Update password"}
        </Submit>
      </form>
      <Footer>
        <Back to={cPath("signin")}>← Back to sign in</Back>
      </Footer>
      </AuthPageShell>
    </>
  );
}
